/**
 * apps/command/src/ui/nosignal.tsx — what a tile looks like when there is
 * nothing to show.
 *
 * ============================================================================
 * ONE DEFINITION, USED BY BOTH WALLS.
 * ============================================================================
 *
 * The camera wall and the screen wall both have tiles that go dark, and they
 * had begun to grow their own copies of the same treatment. Two copies of a
 * visual language is how the two walls end up saying the same thing in
 * different ways, and an operator switching between them has to learn it twice.
 *
 * ============================================================================
 * «NO PICTURE» AND «NO CONNECTION» ARE DIFFERENT FAULTS AND LOOK DIFFERENT.
 * ============================================================================
 *
 * Snow alone says «this input has nothing on it» — true of a camera somebody
 * switched off, and true of a feed that stopped.
 *
 * It is NOT the whole truth about an unreachable kiosk. That machine is not a
 * dead input; it is a device that is not answering, and the difference matters
 * to the person reading the wall: one is a setting, the other is a trip to a
 * building. So an offline tile gets the snow AND a signal that is visibly
 * SEARCHING — arcs that light outward and die, over and over, never completing.
 *
 * The rest between cycles is deliberate. A loop with no pause reads as
 * progress, and progress is exactly what is not happening.
 */

/**
 * The snow, plus the roll band that drifts down a detuned analogue screen.
 *
 * `pointer-events-none` throughout: this is scenery on top of a tile that may
 * itself be a button, and scenery that swallows clicks is a bug nobody
 * attributes to the decoration.
 *
 * `overflow-hidden` on the wrapper because the roll band travels well past the
 * tile at both ends — without it, a bright bar appears over the card's own
 * chrome on its way through.
 */
export function NoSignalLayer(): React.JSX.Element {
  return (
    <span className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <span className="absolute inset-0 animate-snow bg-static opacity-90" />
      <span className="absolute inset-x-0 top-0 h-1/3 animate-roll bg-roll" />
    </span>
  );
}

/**
 * A signal searching and not finding.
 *
 * Three arcs and a source dot, each on the same cycle with its own delay, so
 * the brightening runs outward from the device — the direction a radio actually
 * reaches. Then a slash across it, static, because the arcs alone would read as
 * «connecting» and this is not connecting.
 *
 * The delays are inline `animationDelay` values rather than classes: they are
 * derived from the arc's index, and a utility class per delay would be three
 * arbitrary numbers in a stylesheet for no gain.
 */
export function SearchingSignal(): React.JSX.Element {
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
  };
  return (
    <svg viewBox="0 0 24 24" className="relative h-10 w-10" aria-hidden="true">
      {/* Outermost first in the DOM, but LAST in time — the delays run the
          animation from the source outward regardless of paint order. */}
      <path d="M3.5 9.5a12 12 0 0117 0" {...stroke} className="animate-seek" style={{ animationDelay: '480ms' }} />
      <path d="M6.8 13a7.5 7.5 0 0110.4 0" {...stroke} className="animate-seek" style={{ animationDelay: '320ms' }} />
      <path d="M9.9 16.3a3.2 3.2 0 014.2 0" {...stroke} className="animate-seek" style={{ animationDelay: '160ms' }} />
      <circle cx="12" cy="19.4" r="1.3" fill="currentColor" className="animate-seek" />
      {/* The slash does NOT animate. It is the statement — «this is not
          reaching anything» — and a statement that blinks is a suggestion. */}
      <path d="M3.6 3.6l16.8 16.8" {...stroke} />
    </svg>
  );
}
